import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { ApiService } from '../../shared/services/api.service';
import { AuthService } from '../../shared/services/auth.service';
import { FeatureConfigService } from '../../shared/services/feature-config.service';
import { TranslateService } from '../../shared/services/translate.service';
import { User } from '../../shared/interfaces';
import { MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import { AuthTokenUpdatedEvent } from '../../shared/models';
import { EmitterService } from '../../shared/services/emitter.service';
import { APP_TOKEN_STORAGE_KEY } from '../../shared/constants';

@Injectable({
  providedIn: 'root',
})
export class AuthRedirectService {
  private readonly apiService: ApiService = inject(ApiService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly bridge: MtmAuthBridgeService = inject(MtmAuthBridgeService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly featureConfigService: FeatureConfigService = inject(FeatureConfigService);

  // Login with token. Used for social login and the IAM SDK flow; hydrates the legacy User from
  // the gateway. `identityOverrides` lets the IAM flow supply username/companyId the gateway record
  // may not carry (companyId ← tenant id).
  public loginWithToken(
    token: string,
    username: string,
    brandId?: string,
    identityOverrides?: Partial<User>
  ): Observable<void> {
    // Authorize the user fetch without firing the token event / permission load yet, so the user
    // is hydrated and stored BEFORE roles/permissions and feature config are requested.
    this.authService.setAuthTokenValue(token);
    const loadUserFn: (
      username: string,
      brandId?: string,
      identityOverrides?: Partial<User>
    ) => Observable<User> = identityOverrides?.companyId ? this.loadUser : this.loadIAMUser;
    return loadUserFn.call(this, username, brandId, identityOverrides).pipe(
      switchMap(() => {
        if (identityOverrides?.companyId) {
          this.authService.setAuthToken(token);
          return of(void 0);
        } else {
          localStorage.setItem(APP_TOKEN_STORAGE_KEY, token);
          EmitterService.get(AuthTokenUpdatedEvent).emit(token);
          return of(void 0);
        }
      }),
      switchMap(() => this.loadUserContext(username)),
      map(() => void 0)
    );
  }

  private loadIAMUser(username: string, brandId?: string, identityOverrides?: Partial<User>): Observable<any> {
    return this.bridge.getUser$(username).pipe(
      tap((user: User) => {
        this.applyIdentity(user, username, brandId, identityOverrides);
        localStorage.setItem('auth_user', JSON.stringify(user));
        this.authService.setAuthUser(user);
      })
    );
  }

  private loadUser(username: string, brandId?: string, identityOverrides?: Partial<User>): Observable<any> {
    return this.apiService.httpGet(`/api/users/${username}`).pipe(
      tap((user: User) => {
        this.applyIdentity(user, username, brandId, identityOverrides);
        this.authService.setAuthUser(user);
      })
    );
  }

  private applyIdentity(user: User, username: string, brandId?: string, identityOverrides?: Partial<User>): void {
    user.brandId ??= brandId;
    user.username ||= identityOverrides?.username ?? username;
    user.email ||= identityOverrides?.email ?? username;
    user.firstName ||= identityOverrides?.firstName;
    user.lastName ||= identityOverrides?.lastName;
    user.fullName ||= identityOverrides?.fullName;
    user.fullName ||= [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || undefined;
    // companyId comes from the IAM tenant id (per product requirement), overriding the gateway.
    if (identityOverrides?.companyId) {
      user.companyId = identityOverrides.companyId;
    }
  }

  private loadUserContext(username: string): Observable<void> {
    return forkJoin({
      profileSettings: this.apiService.httpGet(`/api/users/${username}/profile-settings`),
      effectivePermissions: this.authService.updateEffectivePermissions(),
      featureConfig: this.featureConfigService.loadFeatureConfig(),
    }).pipe(
      tap(({ profileSettings }: { profileSettings: any }) => {
        this.applyUserSettings(profileSettings);
      }),
      map(() => void 0)
    );
  }

  private applyUserSettings(profileSettings: any): void {
    this.authService.setAuthUserSettings(profileSettings);
    this.authService.applyDefaultBrand(profileSettings);

    if (profileSettings.language) {
      this.translateService.use(profileSettings.language).then(() => {
        this.authService.setAuthLanguage(profileSettings.language);
      });
    }
  }
}
